import { pxToEm } from 'util/pxToEm';
import React, { ReactElement, useEffect, useState } from 'react';
import useSWR from 'swr';
import { useProfile } from 'hooks';
import { GetSchoolResponse, useSchoolsApi } from 'services/api';
import { NoSchoolDashboardPage } from 'components/pages';
import { ForYouPage } from 'components/pages/home/for-you/ForYouPage';
import { EventsPage } from 'components/pages/home/for-you/sections/events-browse';
import { OrganizationsPage } from 'components/pages/home/for-you/sections/orgs-browse';
import { useRouter } from 'next/router';
import { Button, Modal } from 'components/ui';
import { Box, Typography, useTheme } from '@mui/material';
import { Routes } from 'services/routes';
import { MainLayout } from 'layouts';
import { LogoIcon } from 'components/icons';
import { PendingOrgLeaderInvitesModal } from 'components/modals/PendingOrgLeaderInvitesModal';
import { useOrganizationsApi } from 'services/api/hooks/useOrganizationsApi.hook';

export interface HomeProps {}

export default function Home (): ReactElement {
  const { profile, loading } = useProfile();
  const theme = useTheme();

  const schoolId = profile?.school?.id;

  const schoolsApi = useSchoolsApi();
  const rSchool = useSWR<GetSchoolResponse>(
    schoolId ? [schoolsApi.getSchool, schoolId] : null,
  );

  const organizationsApi = useOrganizationsApi();
  const { data: invites, error: invitesError } = useSWR(
    profile ? 'pending-org-leader-invites' : null,
    () =>
      organizationsApi.getPendingOrgLeaderInvites().then(res => {
        if (!res.success)

          return [];

        return res.value || [];
      }),
  );

  const [modalOpen, setModalOpen] = useState(true);

  useEffect(() => {
    if (profile && invites && invites?.length > 0) setModalOpen(true);
  }, [profile, invites]);

  const router = useRouter();

  const currentTab = router.query.tab;

  const showCompletedSignUpModal =
    profile && 'showCompletedSignUp' in router.query;

  if (loading) {
    return (
      <MainLayout hideSideMenu>
        <Box
          className="CampusLushLogo-logo"
          sx={{
            marginBottom: 2,
            marginTop: 120,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            fontSize: pxToEm(60),
          }}
        >
          <LogoIcon
            color={theme.palette.primary.main}
            color2={theme.palette.vibrant.secondary}
          />
          <Typography variant="body2" mt={40}>
            {'We\'re customizing your experience'}
          </Typography>
        </Box>
      </MainLayout>
    );
  }

  if (!profile?.school) return <NoSchoolDashboardPage />;

  const schoolSlug = rSchool.data?.slug ?? profile.school.slug;

  return (
    <>
      {showCompletedSignUpModal && (
        <Modal
          open
          title={`${profile.firstName}, your account setup is complete!`}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography>
              Start discovering the campus events and organizations you’re
              interested in.
            </Typography>
            <Box mt={24}>
              <Button
                style={{
                  width: 300,
                  fontSize: pxToEm(14),
                  fontFamily: 'Poppins',
                }}
                variant="vibrant"
                onClick={() => router.replace(Routes.home())}
              >
                Let&apos;s Go
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
      <PendingOrgLeaderInvitesModal
        open={Boolean(invites?.length) && modalOpen && !invitesError}
        invites={(invites || []) as any}
        onClose={() => setModalOpen(false)}
      />
      {currentTab === 'events' ? (
        <EventsPage slug={schoolSlug} school={rSchool.data} />
      ) : currentTab === 'organizations' ? (
        <OrganizationsPage slug={schoolSlug} school={rSchool.data} />
      ) : (
        <ForYouPage slug={schoolSlug} school={rSchool.data} />
      )}
    </>
  );
}
